import { useState } from "react";
import api, { formatError } from "@/api/client";
import { uploadSpaceMedia } from "@/lib/media";
import { toast } from "sonner";
import { Upload } from "lucide-react";

const FONTS = ["Outfit", "Inter", "IBM Plex Sans", "Merriweather", "Roboto", "Lora", "Source Sans 3", "Poppins"];

export default function AppearanceTab({ space, onReload }) {
  const [b, setB] = useState({ ...space.branding });
  const [footerText, setFooterText] = useState(space.settings?.footer_text || "");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setB({ ...b, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/spaces/${space.id}`, {
        branding: b,
        settings: { ...space.settings, footer_text: footerText },
      });
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const upload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadSpaceMedia(space.id, file);
      set(field, url);
      toast.success("Uploaded");
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="p-6 max-w-7xl">
      <h2 className="text-2xl font-black tracking-tight mb-6">Appearance</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-5">
          <div>
            <label className="block text-sm font-bold mb-2">Logo (light mode)</label>
            <div className="flex items-center gap-3">
              {b.logo_url && <img src={b.logo_url} alt="logo" className="h-10 border border-zinc-200 rounded-sm bg-white p-1" />}
              <label className="cursor-pointer border border-zinc-300 hover:bg-zinc-50 px-3 py-2 rounded-sm text-sm flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload light logo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "logo_url")} data-testid="upload-logo" />
              </label>
              {b.logo_url && <button onClick={() => set("logo_url", "")} className="text-xs text-rose-600">Remove</button>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold mb-2">Logo (dark mode)</label>
            <div className="flex items-center gap-3">
              {b.logo_url_dark && <img src={b.logo_url_dark} alt="logo-dark" className="h-10 border border-zinc-200 rounded-sm bg-zinc-900 p-1" />}
              <label className="cursor-pointer border border-zinc-300 hover:bg-zinc-50 px-3 py-2 rounded-sm text-sm flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload dark logo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "logo_url_dark")} data-testid="upload-logo-dark" />
              </label>
              {b.logo_url_dark && <button onClick={() => set("logo_url_dark", "")} className="text-xs text-rose-600">Remove</button>}
            </div>
            <p className="text-xs text-zinc-500 mt-1">Used when visitors switch the public portal to dark mode. Falls back to the light logo if unset.</p>
          </div>
          <div>
            <label className="block text-sm font-bold mb-2">Favicon</label>
            <div className="flex items-center gap-3">
              {b.favicon_url && <img src={b.favicon_url} alt="favicon" className="h-8 w-8 border border-zinc-200 rounded-sm bg-white p-1" />}
              <label className="cursor-pointer border border-zinc-300 hover:bg-zinc-50 px-3 py-2 rounded-sm text-sm flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload favicon
                <input type="file" accept="image/*,.ico" className="hidden" onChange={(e) => upload(e, "favicon_url")} />
              </label>
            </div>
          </div>

          <ColorRow label="Primary color" value={b.primary_color} onChange={(v) => set("primary_color", v)} testid="color-primary" />
          <ColorRow label="Background" value={b.background_color} onChange={(v) => set("background_color", v)} testid="color-bg" />
          <ColorRow label="Text" value={b.text_color} onChange={(v) => set("text_color", v)} testid="color-text" />

          <div>
            <label className="block text-sm font-bold mb-2">Font family</label>
            <select value={b.font_family || "Outfit"} onChange={(e) => set("font_family", e.target.value)}
              data-testid="font-family-select"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm">
              {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Footer text</label>
            <input value={footerText} onChange={(e) => setFooterText(e.target.value)}
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Custom CSS</label>
            <textarea value={b.custom_css || ""} onChange={(e) => set("custom_css", e.target.value)}
              rows={4} placeholder="/* injected into public portal */"
              className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-xs font-mono" />
          </div>

          <button onClick={save} disabled={saving} data-testid="appearance-save"
            className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold px-4 py-2.5 rounded-sm disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-3">Live preview</div>
          <div className="border border-zinc-200 rounded-sm overflow-hidden" style={{ background: b.background_color }}>
            <div className="px-4 py-3 border-b flex items-center gap-2 backdrop-blur-xl bg-white/85">
              {b.logo_url ? (
                <img src={b.logo_url} alt="logo" className="h-6" />
              ) : (
                <div className="w-6 h-6 rounded-sm flex items-center justify-center text-white text-xs font-black" style={{ background: b.primary_color }}>
                  {space.name.charAt(0)}
                </div>
              )}
              <div style={{ fontFamily: b.font_family || "Outfit", color: b.text_color }} className="text-sm font-bold">{space.name}</div>
            </div>
            <div className="p-8 text-center" style={{ color: b.text_color }}>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: b.font_family || "Outfit" }}>How can we help?</h2>
              <p className="text-sm opacity-70 mb-4">Find answers fast.</p>
              <button className="px-4 py-2 rounded-sm text-white text-sm" style={{ background: b.primary_color }}>Browse articles</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-bold mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-12 h-10 rounded-sm border border-zinc-300 cursor-pointer" />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2.5 border border-zinc-300 rounded-sm text-sm font-mono" />
      </div>
    </div>
  );
}
